import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, Link, Paragraph } from '@digdir/designsystemet-react';
import type { ShareKind } from '../../../api/workEnvironmentTypes';
import { Routes } from '../../../enums/Routes';
import {
  useShareWithTeamMutation,
  useTeamSharesQuery,
} from '../../../hooks/queries/useTeamSharesQuery';
import { buildRoute } from '../../../utils/routeBuilder';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { canSend, channelList, lastShare } from '../sharingView';
import { dayAndMonth } from '../surveyProcessView';
import classes from '../WorkEnvironmentSurveyProcess.module.css';

export type Sharing = { surveyId: number; teamId: number; kind?: ShareKind };

/** Which channels the team gets it on, when it was sent, and a way to send it again */
export const ShareStatus = ({
  surveyId,
  teamId,
  kind,
}: Sharing): ReactElement | null => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const { data } = useTeamSharesQuery(surveyId);
  const send = useShareWithTeamMutation(surveyId);
  if (!data) return null;
  const last = kind ? lastShare(data, kind) : null;

  return (
    <>
      {data.channels.length > 0 ? (
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('workEnvironmentSharing.onChannels', {
            channels: channelList(data.channels, t),
          })}
        </Paragraph>
      ) : (
        <Alert data-color="info" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentSharing.noChannels')}
          </Paragraph>
          {may('share') && (
            <Link asChild data-size="sm">
              <RouterLink to={buildRoute(Routes.OrganizationTeam, { teamId })}>
                {t('workEnvironmentSharing.addChannels')}
              </RouterLink>
            </Link>
          )}
        </Alert>
      )}
      {last && (
        <Paragraph data-size="sm" role="status">
          {t(
            last.automatic
              ? 'workEnvironmentSharing.sentAutomatically'
              : 'workEnvironmentSharing.sent',
            {
              date: dayAndMonth(last.sharedAt, i18n.language),
            },
          )}
        </Paragraph>
      )}
      {send.isError && (
        <Alert data-color="danger" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentSharing.failed')}
          </Paragraph>
        </Alert>
      )}
      {kind && may('share') && canSend(data, kind) && (
        <div>
          <Button
            variant="secondary"
            data-size="sm"
            loading={send.isPending}
            onClick={() => send.mutate(kind)}
          >
            {t(
              last
                ? 'workEnvironmentSharing.sendAgain'
                : 'workEnvironmentSharing.send',
            )}
          </Button>
        </div>
      )}
    </>
  );
};
